import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, AlertCircle, Edit, Eye, EyeOff, FileText } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import { ImagePicker } from './shared';
import PortalSectionCheckboxGrid from './shared/PortalSectionCheckboxGrid';

// ProgramForm is defined at MODULE LEVEL to prevent re-creation on parent re-renders
// This is critical for preserving form state during async operations like image upload
const ProgramForm = ({ program, onSave, onCancel, setMessage }) => {
  const [programData, setProgramData] = useState(program ? {
    ...program,
    features: program.features || [],
    visible_portal_sections: program.visible_portal_sections ?? null,
  } : {
    title: '',
    description: '',
    age_range: '',
    features: [],
    image_url: '',
    link_url: '',
    order: 0,
    visible: true,
    visible_portal_sections: null,
  });

  const addFeature = () => {
    setProgramData({...programData, features: [...(programData.features || []), '']});
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...(programData.features || [])];
    newFeatures[index] = value;
    setProgramData({...programData, features: newFeatures.filter(f => f.trim())});
  };

  const removeFeature = (index) => {
    const newFeatures = [...(programData.features || [])];
    newFeatures.splice(index, 1);
    setProgramData({...programData, features: newFeatures});
  };

  return (
    <Card className="mb-6 admin-card admin-corner-accent border-stone-200">
      <CardHeader className="border-b border-stone-200 bg-gradient-to-r from-stone-50 to-amber-50/30">
        <CardTitle className="text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <span className="text-amber-600">●</span>
          {program?.id ? 'Edit Program' : 'Add New Program'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="title" className="text-gray-800 dark:text-gray-200 font-medium">Program Title</Label>
            <Input
              id="title"
              value={programData.title}
              onChange={(e) => setProgramData({...programData, title: e.target.value})}
              placeholder="Kids (Ages 4-6)"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
            />
          </div>

          <div>
            <Label htmlFor="age_range" className="text-gray-800 dark:text-gray-200 font-medium">Age Range</Label>
            <Input
              id="age_range"
              value={programData.age_range}
              onChange={(e) => setProgramData({...programData, age_range: e.target.value})}
              placeholder="4-6 years"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="link_url" className="text-gray-800 dark:text-gray-200 font-medium">Learn More Link (optional)</Label>
          <Input
            id="link_url"
            value={programData.link_url || ''}
            onChange={(e) => setProgramData({...programData, link_url: e.target.value})}
            placeholder="/programs/tai-chi — leave blank to call the dojo"
            className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="visible"
            checked={programData.visible !== false}
            onCheckedChange={(checked) => setProgramData({...programData, visible: checked})}
          />
          <Label htmlFor="visible" className="flex items-center space-x-2">
            {programData.visible !== false ? (
              <>
                <Eye className="h-4 w-4" />
                <span>Visible on website</span>
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4" />
                <span>Hidden from website</span>
              </>
            )}
          </Label>
        </div>

        <div>
          <Label htmlFor="description" className="text-gray-800 dark:text-gray-200 font-medium">Description</Label>
          <Textarea
            id="description"
            value={programData.description}
            onChange={(e) => setProgramData({...programData, description: e.target.value})}
            placeholder="Program description..."
            rows={3}
            className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
          />
        </div>

        <div>
          <Label className="text-gray-800 dark:text-gray-200 font-medium">Features</Label>
          {(programData.features || []).length === 0 ? (
            <Button
              type="button"
              variant="adminSecondary"
              size="sm"
              onClick={addFeature}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feature
            </Button>
          ) : (
            (programData.features || []).map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 mt-2">
                <Input
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  placeholder="Feature description"
                  className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
                />
                <Button
                  type="button"
                  variant="adminOutline"
                  size="sm"
                  onClick={() => removeFeature(index)}
                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                {index === (programData.features || []).length - 1 && (
                  <Button
                    type="button"
                    variant="adminSecondary"
                    size="sm"
                    onClick={addFeature}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        <div>
          <Label className="text-gray-800 dark:text-gray-200 font-medium mb-2 block">Member Portal Sections</Label>
          <PortalSectionCheckboxGrid
            value={programData.visible_portal_sections}
            onChange={(sections) => setProgramData({...programData, visible_portal_sections: sections})}
          />
        </div>

        <ImagePicker
          value={programData.image_url}
          onChange={(url) => setProgramData({...programData, image_url: url})}
          context="programs"
          label="Program Image"
          previewSize="md"
        />

        <div className="flex space-x-2 pt-4 border-t border-stone-200">
          <Button onClick={() => onSave(programData)} variant="adminPrimary">
            <Save className="h-4 w-4 mr-2" />
            Save Program
          </Button>
          <Button variant="adminOutline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ProgramsEditor = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/programs`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      setMessage('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (programData) => {
    try {
      const url = programData.id ?
        `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programData.id}` :
        `${process.env.REACT_APP_BACKEND_URL}/api/programs`;

      const method = programData.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(programData)
      });

      if (response.ok) {
        setMessage('Program saved successfully!');
        setEditingProgram(null);
        fetchPrograms();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Failed to save: ${response.status}`;
        throw new Error(errorMsg);
      }
    } catch (error) {
      setMessage(`Failed to save program: ${error.message}`);
      // Don't close the form - keep editingProgram state so user can fix issues
      console.error('Save error:', error);
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('Program deleted successfully!');
        fetchPrograms();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Failed to delete program');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Programs"
        description="Manage martial arts programs and their details"
        icon={<FileText className="h-6 w-6" />}
        actions={
          !editingProgram && (
            <Button
              onClick={() => setEditingProgram({})}
              variant="adminPrimary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Program
            </Button>
          )
        }
      />

      {message && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.includes('success') ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' :
          'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <AlertCircle className="h-4 w-4" />
          <span>{message}</span>
        </div>
      )}

      {editingProgram && (
        <ProgramForm
          program={editingProgram}
          onSave={handleSave}
          onCancel={() => setEditingProgram(null)}
          setMessage={setMessage}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {programs.map((program) => (
          <Card key={program.id} className="admin-card admin-corner-accent border-stone-200 hover:border-amber-500 transition-all">
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                {program.image_url && (
                  <img
                    src={program.image_url}
                    alt={program.title}
                    className="w-16 h-16 object-cover rounded-lg border-2 border-stone-200"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{program.title}</h3>
                    {program.visible !== false ? (
                      <Eye className="h-4 w-4 text-green-600" title="Visible on website" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-stone-400" title="Hidden from website" />
                    )}
                  </div>
                  <p className="text-sm text-stone-600 dark:text-stone-400 mb-2">{program.age_range}</p>
                  <p className="text-sm text-stone-700 dark:text-stone-300 line-clamp-2">{program.description}</p>
                  {program.visible === false && (
                    <p className="text-xs text-amber-700 mt-1 font-medium">Hidden from public view</p>
                  )}
                  <div className="flex space-x-2 mt-3">
                    <Button
                      size="sm"
                      variant="adminSecondary"
                      onClick={() => setEditingProgram(program)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="adminOutline"
                      onClick={() => handleDelete(program.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProgramsEditor;
